import React, { useState, useEffect } from 'react';
import { 
  Check, Crown, Zap, ArrowRight, Loader2, 
  Receipt, BarChart3, Users, Megaphone, Download,
  Calendar, CreditCard, TrendingUp, AlertTriangle,
  ArrowDown, ArrowUp, X, DollarSign, Wallet
} from 'lucide-react';
import api from '../../../lib/api';

const TIER_DETAILS = {
  free: {
    name: 'Starter',
    price: 0,
    color: 'var(--text-muted)',
    features: ['Up to 25 employees', '1 active campaign', 'Basic impact portal', '3 AI charity matches', 'Basic analytics', '1.8M+ charity access']
  },
  professional: {
    name: 'Professional',
    price: 99,
    color: '#2563EB',
    features: ['Up to 250 employees', 'Unlimited campaigns', 'Custom branded portal', 'Unlimited AI matching', 'Full analytics & reports', 'Employer matching engine', 'ESG scoring', 'Email support']
  },
  enterprise: {
    name: 'Enterprise',
    price: 299,
    color: '#7C3AED',
    features: ['Unlimited employees', 'Unlimited campaigns', 'White-label portal (your domain)', 'Priority AI matching', 'Advanced analytics & exports', 'Employer matching engine', 'Dedicated support', 'API access & SSO']
  }
};

const SubscriptionView = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [billing, setBilling] = useState(null);
  const [usage, setUsage] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [downgrading, setDowngrading] = useState(false);
  const [previewModal, setPreviewModal] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => { loadBillingData(); }, []);

  const loadBillingData = async () => {
    try {
      const [billRes, usageRes, invRes] = await Promise.all([
        api.get('/api/billing/summary'),
        api.get('/api/billing/usage'),
        api.get('/api/billing/invoices')
      ]);
      setBilling(billRes.data);
      setUsage(usageRes.data);
      setInvoices(invRes.data.invoices || []);
    } catch (err) {
      console.error('Failed to load billing data', err);
      setBilling({ tier: 'free', plan_name: 'Starter', price: 0, status: 'active' });
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewChange = async (targetTier) => {
    setPreviewLoading(true);
    try {
      const res = await api.post('/api/subscriptions/preview-change', { target_tier: targetTier });
      setPreviewModal(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to preview plan change');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirmUpgrade = async () => {
    const tier = previewModal?.target_tier;
    if (!tier) return;
    setUpgrading(tier);
    setPreviewModal(null);
    try {
      const res = await api.post('/api/payments/checkout', { tier, origin_url: window.location.origin });
      if (res.data.paid_by_credit) {
        alert(res.data.message);
        loadBillingData();
        setUpgrading(null);
      } else {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to start checkout');
      setUpgrading(null);
    }
  };

  const handleConfirmDowngrade = async () => {
    const tier = previewModal?.target_tier;
    if (!tier) return;
    setDowngrading(true);
    setPreviewModal(null);
    try {
      const res = await api.post('/api/subscriptions/downgrade', { target_tier: tier });
      alert(res.data.message);
      loadBillingData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to downgrade');
    } finally {
      setDowngrading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel your subscription? You\'ll keep access until the billing period ends.')) return;
    try {
      await api.post('/api/subscriptions/cancel', { reason: 'user_cancelled' });
      loadBillingData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to cancel');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }

  const currentTier = billing?.tier || 'free';
  const tierOrder = ['free', 'professional', 'enterprise'];
  const currentIdx = tierOrder.indexOf(currentTier);
  const tierColor = TIER_DETAILS[currentTier]?.color || '#6B7280';

  const UsageBar = ({ used, limit, unlimited, label, icon: Icon }) => {
    const pct = unlimited ? 15 : (limit > 0 ? Math.min((used / limit) * 100, 100) : 0);
    const isNearLimit = !unlimited && limit > 0 && pct >= 80;
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">{label}</span>
          </div>
          <span className="text-sm font-bold text-gray-900">{used} / {unlimited ? <span className="text-gray-400">Unlimited</span> : limit}</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: isNearLimit ? '#EF4444' : tierColor }} />
        </div>
        {isNearLimit && (
          <div className="flex items-center gap-1 mt-2 text-xs text-red-500">
            <AlertTriangle className="w-3 h-3" /><span>Approaching limit — upgrade for more</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="subscription-view">
      {/* Tab Nav */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-fit">
        {[{ id: 'overview', label: 'Overview' }, { id: 'plans', label: 'Plans' }, { id: 'invoices', label: 'Invoices' }].map(tab => (
          <button key={tab.id} data-testid={`billing-tab-${tab.id}`} onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ═══════ OVERVIEW TAB ═══════ */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Plan Summary Card */}
          <div className="rounded-2xl p-6 border-2" style={{ borderColor: tierColor + '40', background: tierColor + '06' }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ background: tierColor + '18' }}>
                  <Crown className="w-7 h-7" style={{ color: tierColor }} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-xl font-bold text-gray-900">{TIER_DETAILS[currentTier]?.name} Plan</h3>
                    <span className="px-2.5 py-0.5 text-xs font-bold rounded-full text-white" style={{ background: tierColor }}>
                      {billing?.cancel_at_period_end ? 'CANCELLING' : billing?.status === 'active' ? 'ACTIVE' : billing?.status?.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    {currentTier === 'free' ? 'Free forever' : `$${TIER_DETAILS[currentTier]?.price}/month`}
                    {billing?.next_billing_date && !billing?.cancel_at_period_end && (
                      <span className="ml-2 text-gray-400">Next billing: {new Date(billing.next_billing_date).toLocaleDateString()}</span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {currentTier !== 'enterprise' && (
                  <button data-testid="overview-upgrade-btn" onClick={() => setActiveTab('plans')}
                    className="px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition-all flex items-center gap-1.5">
                    <Zap className="w-3.5 h-3.5" /> Upgrade
                  </button>
                )}
                {currentTier !== 'free' && !billing?.cancel_at_period_end && (
                  <button data-testid="cancel-subscription-btn" onClick={handleCancel} className="text-sm text-gray-400 hover:text-red-500 transition-colors">Cancel</button>
                )}
              </div>
            </div>
            {billing?.downgrade_scheduled && billing?.downgrade_to && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-2 text-sm text-amber-700">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                <span>Scheduled downgrade to <strong>{TIER_DETAILS[billing.downgrade_to]?.name}</strong> at end of billing period</span>
              </div>
            )}
          </div>

          {/* Stats Row */}
          <div className={`grid gap-4 ${billing?.account_credit > 0 ? 'grid-cols-4' : 'grid-cols-3'}`}>
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-center gap-2 mb-1"><CreditCard className="w-4 h-4 text-gray-400" /><span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Spent</span></div>
              <p className="text-2xl font-bold text-gray-900">${(billing?.total_spent || 0).toFixed(2)}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-center gap-2 mb-1"><Receipt className="w-4 h-4 text-gray-400" /><span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Invoices</span></div>
              <p className="text-2xl font-bold text-gray-900">{billing?.invoice_count || 0}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-center gap-2 mb-1"><TrendingUp className="w-4 h-4 text-gray-400" /><span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Donations</span></div>
              <p className="text-2xl font-bold text-gray-900">${(usage?.donations?.total_amount || 0).toLocaleString()}</p>
              <p className="text-xs text-gray-400">{usage?.donations?.count || 0} donations</p>
            </div>
            {billing?.account_credit > 0 && (
              <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-5">
                <div className="flex items-center gap-2 mb-1"><Wallet className="w-4 h-4 text-emerald-600" /><span className="text-xs font-medium text-emerald-600 uppercase tracking-wide">Account Credit</span></div>
                <p className="text-2xl font-bold text-emerald-700">${billing.account_credit.toFixed(2)}</p>
                <p className="text-xs text-emerald-500">Applied on next upgrade</p>
              </div>
            )}
          </div>

          {/* Usage Section */}
          {usage && (
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Usage</h4>
              <div className="grid grid-cols-2 gap-4">
                <UsageBar used={usage.campaigns?.used || 0} limit={usage.campaigns?.limit || 1} unlimited={usage.campaigns?.unlimited} label="Active Campaigns" icon={Megaphone} />
                <UsageBar used={usage.employees?.used || 0} limit={usage.employees?.limit || 25} unlimited={usage.employees?.unlimited} label="Team Members" icon={Users} />
              </div>
            </div>
          )}

          {/* Recent Invoices Preview */}
          {invoices.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-gray-900">Recent Invoices</h4>
                <button onClick={() => setActiveTab('invoices')} className="text-xs text-blue-600 hover:underline">View all</button>
              </div>
              <div className="bg-white rounded-xl border border-gray-100 divide-y divide-gray-50">
                {invoices.slice(0, 3).map(inv => (
                  <div key={inv.invoice_id} className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Receipt className="w-4 h-4 text-gray-300" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{inv.invoice_number}</p>
                        <p className="text-xs text-gray-400">
                          {new Date(inv.created_at).toLocaleDateString()}
                          {inv.invoice_type && inv.invoice_type !== 'subscription' && (
                            <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              inv.invoice_type === 'upgrade' ? 'bg-blue-50 text-blue-600' : 
                              inv.invoice_type === 'downgrade' ? 'bg-amber-50 text-amber-600' : 'bg-gray-50 text-gray-500'
                            }`}>{inv.invoice_type}</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-bold text-gray-900">${inv.amount?.toFixed(2)}</span>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        inv.status === 'paid' ? 'bg-emerald-50 text-emerald-700' : 
                        inv.status === 'adjustment' ? 'bg-blue-50 text-blue-700' : 'bg-gray-50 text-gray-600'
                      }`}>{inv.status === 'adjustment' ? 'Credit' : 'Paid'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══════ PLANS TAB ═══════ */}
      {activeTab === 'plans' && (
        <div className="space-y-6">
          <div className="grid md:grid-cols-3 gap-6">
            {tierOrder.map((tier, idx) => {
              const details = TIER_DETAILS[tier];
              const isCurrent = tier === currentTier;
              const isUpgrade = idx > currentIdx;

              return (
                <div key={tier} data-testid={`plan-card-${tier}`}
                  className={`rounded-2xl p-6 flex flex-col transition-all ${isCurrent ? 'border-2 shadow-lg' : 'border border-gray-200 hover:shadow-md'}`}
                  style={isCurrent ? { borderColor: details.color } : {}}>
                  {tier === 'professional' && (
                    <div className="text-center -mt-9 mb-3">
                      <span className="bg-gray-900 text-white text-xs font-bold px-4 py-1 rounded-full">MOST POPULAR</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-base font-bold text-gray-900">{details.name}</h4>
                    {isCurrent && <span className="text-xs font-bold px-2 py-0.5 rounded-full text-white" style={{ background: details.color }}>CURRENT</span>}
                  </div>
                  <div className="mb-5">
                    <span className="text-3xl font-extrabold text-gray-900">${details.price}</span>
                    <span className="text-sm text-gray-500">/month</span>
                  </div>
                  <ul className="space-y-2.5 mb-6 flex-1">
                    {details.features.map((f, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                        <Check className="w-3.5 h-3.5 flex-shrink-0" style={{ color: details.color }} />{f}
                      </li>
                    ))}
                  </ul>
                  {isCurrent ? (
                    <div className="w-full py-3 rounded-xl text-center text-sm font-semibold text-gray-400 bg-gray-100">Current plan</div>
                  ) : isUpgrade ? (
                    <button data-testid={`upgrade-to-${tier}-btn`}
                      onClick={() => handlePreviewChange(tier)}
                      disabled={upgrading === tier || previewLoading}
                      className="w-full py-3 rounded-xl font-semibold text-sm text-white transition-all hover:shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
                      style={{ background: details.color }}>
                      {upgrading === tier ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</> :
                       previewLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Loading...</> :
                       <>Upgrade <ArrowRight className="w-4 h-4" /></>}
                    </button>
                  ) : (
                    <button data-testid={`downgrade-to-${tier}-btn`}
                      onClick={() => handlePreviewChange(tier)}
                      disabled={downgrading || previewLoading}
                      className="w-full py-3 rounded-xl font-semibold text-sm text-gray-500 border border-gray-200 hover:border-gray-400 hover:text-gray-700 transition-all disabled:opacity-50">
                      {downgrading ? 'Processing...' : previewLoading ? 'Loading...' : 'Downgrade'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
            <h4 className="font-bold text-gray-900 mb-4">Common questions</h4>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { q: 'Can I cancel anytime?', a: 'Yes, cancel anytime. You keep access until your billing period ends.' },
                { q: 'What happens to my data if I downgrade?', a: 'Your data is always safe. Some features may become read-only on lower tiers.' },
                { q: 'Do you take a cut from donations?', a: 'Never — Altrunic takes 0% of donations. Every.org processes and receipts them at 0% platform fee; only card processing (2.2% + $0.30) applies unless the donor covers it. Bank transfers reach the charity at 100%.' },
                { q: 'How does proration work on upgrades?', a: 'You get credit for unused days on your current plan. We only charge the difference for the remainder of your billing cycle.' },
                { q: 'What about downgrades?', a: 'Unused days on your current plan are converted to account credit, automatically applied on your next invoice.' },
                { q: 'Can I switch plans mid-cycle?', a: 'Yes! Both upgrades and downgrades take effect immediately with fair proration.' },
              ].map((faq, i) => (
                <div key={i}>
                  <p className="text-sm font-semibold text-gray-800">{faq.q}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ═══════ INVOICES TAB ═══════ */}
      {activeTab === 'invoices' && (
        <div className="space-y-4">
          {invoices.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
              <Receipt className="w-10 h-10 text-gray-200 mx-auto mb-3" />
              <h4 className="font-semibold text-gray-900 mb-1">No invoices yet</h4>
              <p className="text-sm text-gray-400">Invoices will appear here after your first payment.</p>
              {currentTier === 'free' && (
                <button onClick={() => setActiveTab('plans')} className="mt-4 px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition-all">
                  Upgrade to Professional
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border border-gray-100 p-4">
                  <div className="flex items-center gap-2 mb-1"><Receipt className="w-4 h-4 text-gray-400" /><span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Invoices</span></div>
                  <p className="text-xl font-bold text-gray-900">{invoices.length}</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-100 p-4">
                  <div className="flex items-center gap-2 mb-1"><CreditCard className="w-4 h-4 text-gray-400" /><span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Paid</span></div>
                  <p className="text-xl font-bold text-gray-900">
                    ${invoices.filter(i => i.amount > 0).reduce((sum, inv) => sum + (inv.amount || 0), 0).toFixed(2)}
                  </p>
                </div>
                <div className="bg-white rounded-xl border border-gray-100 p-4">
                  <div className="flex items-center gap-2 mb-1"><Calendar className="w-4 h-4 text-gray-400" /><span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Last Payment</span></div>
                  <p className="text-xl font-bold text-gray-900">
                    {invoices[0]?.created_at ? new Date(invoices[0].created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50/50">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Invoice</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Type</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Plan</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Amount</th>
                      <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {invoices.map(inv => (
                      <tr key={inv.invoice_id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-5 py-4">
                          <p className="text-sm font-semibold text-gray-900">{inv.invoice_number}</p>
                          <p className="text-xs text-gray-400">{inv.business_name}</p>
                        </td>
                        <td className="px-5 py-4">
                          <p className="text-sm text-gray-600">{new Date(inv.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</p>
                        </td>
                        <td className="px-5 py-4">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                            inv.invoice_type === 'upgrade' ? 'bg-blue-50 text-blue-700' :
                            inv.invoice_type === 'downgrade' ? 'bg-amber-50 text-amber-700' :
                            'bg-gray-50 text-gray-600'
                          }`}>
                            {inv.invoice_type === 'upgrade' ? 'Upgrade' : inv.invoice_type === 'downgrade' ? 'Downgrade' : 'Subscription'}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <span className="inline-flex items-center gap-1.5 text-sm text-gray-700">
                            <Crown className="w-3.5 h-3.5" style={{ color: TIER_DETAILS[inv.tier]?.color || '#6B7280' }} />
                            {inv.plan_name}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <p className="text-sm font-bold text-gray-900">${inv.amount?.toFixed(2)}</p>
                        </td>
                        <td className="px-5 py-4 text-center">
                          <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                            inv.status === 'paid' ? 'bg-emerald-50 text-emerald-700' : 
                            inv.status === 'adjustment' ? 'bg-blue-50 text-blue-700' :
                            inv.status === 'pending' ? 'bg-amber-50 text-amber-700' : 'bg-gray-50 text-gray-600'
                          }`}>
                            {inv.status === 'paid' ? 'Paid' : inv.status === 'adjustment' ? 'Credit' : inv.status}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <button data-testid={`view-invoice-${inv.invoice_id}`} onClick={() => setSelectedInvoice(inv)}
                            className="text-xs text-blue-600 hover:underline font-medium">View details</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Invoice Detail Modal */}
          {selectedInvoice && (
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onMouseDown={(e) => e.target === e.currentTarget && setSelectedInvoice(null)}>
              <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8" data-testid="invoice-detail-modal">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Invoice</h3>
                    <p className="text-sm text-gray-500">{selectedInvoice.invoice_number}</p>
                    {selectedInvoice.invoice_type && selectedInvoice.invoice_type !== 'subscription' && (
                      <span className={`mt-1 inline-block px-2 py-0.5 text-xs font-medium rounded-full ${
                        selectedInvoice.invoice_type === 'upgrade' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'
                      }`}>Plan {selectedInvoice.invoice_type === 'upgrade' ? 'Upgrade' : 'Downgrade'}</span>
                    )}
                  </div>
                  <button onClick={() => setSelectedInvoice(null)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm"><span className="text-gray-500">Billed to</span><span className="font-medium text-gray-900">{selectedInvoice.business_name}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-gray-500">Email</span><span className="font-medium text-gray-900">{selectedInvoice.user_email}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-gray-500">Date issued</span><span className="font-medium text-gray-900">{new Date(selectedInvoice.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span></div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Plan</span>
                    <span className="font-medium text-gray-900 flex items-center gap-1">
                      <Crown className="w-3 h-3" style={{ color: TIER_DETAILS[selectedInvoice.tier]?.color || '#6B7280' }} />
                      {selectedInvoice.plan_name}
                    </span>
                  </div>
                  {selectedInvoice.description && (
                    <div className="flex justify-between text-sm"><span className="text-gray-500">Description</span><span className="font-medium text-gray-900 text-right max-w-[60%]">{selectedInvoice.description}</span></div>
                  )}
                </div>

                <div className="border-t border-gray-100 pt-4 mb-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-gray-500"><th className="text-left py-2 font-medium">Item</th><th className="text-right py-2 font-medium">Total</th></tr>
                    </thead>
                    <tbody>
                      {(selectedInvoice.items || []).map((item, i) => (
                        <tr key={i} className="border-t border-gray-50">
                          <td className="py-3 text-gray-900 pr-4">{item.description}</td>
                          <td className={`py-3 text-right font-medium whitespace-nowrap ${item.total < 0 ? 'text-emerald-600' : 'text-gray-900'}`}>
                            {item.total < 0 ? `-$${Math.abs(item.total).toFixed(2)}` : `$${item.total?.toFixed(2)}`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="border-t-2 border-gray-900 pt-3 flex justify-between items-center">
                  <span className="text-base font-bold text-gray-900">{selectedInvoice.amount >= 0 ? 'Total' : 'Credit'}</span>
                  <span className="text-xl font-extrabold text-gray-900">${selectedInvoice.amount?.toFixed(2)} USD</span>
                </div>

                <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
                  <span className={`px-3 py-1 text-xs font-bold rounded-full ${
                    selectedInvoice.status === 'paid' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'
                  }`}>{selectedInvoice.status === 'adjustment' ? 'CREDIT APPLIED' : 'PAID'}</span>
                  <button onClick={() => window.print()} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors">
                    <Download className="w-3.5 h-3.5" /> Print / Download
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══════ PRORATION PREVIEW MODAL ═══════ */}
      {previewModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onMouseDown={(e) => e.target === e.currentTarget && setPreviewModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" data-testid="proration-preview-modal">
            {/* Header */}
            <div className={`px-6 py-4 ${previewModal.is_upgrade ? 'bg-gradient-to-r from-blue-600 to-violet-600' : 'bg-gradient-to-r from-amber-500 to-orange-500'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-white">
                  {previewModal.is_upgrade ? <ArrowUp className="w-5 h-5" /> : <ArrowDown className="w-5 h-5" />}
                  <h3 className="font-bold text-lg">{previewModal.is_upgrade ? 'Upgrade' : 'Downgrade'} Plan</h3>
                </div>
                <button onClick={() => setPreviewModal(null)} className="text-white/80 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <p className="text-white/80 text-sm mt-1">{previewModal.current_plan} → {previewModal.target_plan}</p>
            </div>

            <div className="p-6 space-y-4">
              {/* Plan comparison */}
              <div className="flex items-center gap-3">
                <div className="flex-1 text-center p-3 bg-gray-50 rounded-xl">
                  <p className="text-xs text-gray-500 mb-1">Current</p>
                  <p className="font-bold text-gray-900">{previewModal.current_plan}</p>
                  <p className="text-sm text-gray-500">${previewModal.current_price}/mo</p>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <div className="flex-1 text-center p-3 rounded-xl" style={{ background: TIER_DETAILS[previewModal.target_tier]?.color + '10', border: `1px solid ${TIER_DETAILS[previewModal.target_tier]?.color}30` }}>
                  <p className="text-xs text-gray-500 mb-1">New Plan</p>
                  <p className="font-bold text-gray-900">{previewModal.target_plan}</p>
                  <p className="text-sm" style={{ color: TIER_DETAILS[previewModal.target_tier]?.color }}>${previewModal.target_price}/mo</p>
                </div>
              </div>

              {/* Proration breakdown */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-2.5">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Billing Adjustment</p>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Days used on {previewModal.current_plan}</span>
                  <span className="font-medium text-gray-900">{previewModal.days_used} of {previewModal.billing_cycle_days} days</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Days remaining in cycle</span>
                  <span className="font-medium text-gray-900">{previewModal.days_remaining} days</span>
                </div>
                <div className="border-t border-gray-200 pt-2 space-y-1.5">
                  {previewModal.is_upgrade ? (
                    <>
                      {previewModal.credit_for_unused > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-emerald-600">Credit for unused {previewModal.current_plan} days</span>
                          <span className="font-medium text-emerald-600">-${previewModal.credit_for_unused.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">{previewModal.target_plan} for {previewModal.days_remaining} days</span>
                        <span className="font-medium text-gray-900">${previewModal.charge_for_remaining.toFixed(2)}</span>
                      </div>
                      {previewModal.credit_from_account > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-emerald-600">Account credit applied</span>
                          <span className="font-medium text-emerald-600">-${previewModal.credit_from_account.toFixed(2)}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">{previewModal.current_plan} used ({previewModal.days_used} days)</span>
                        <span className="font-medium text-gray-900">${previewModal.cost_of_days_used.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-emerald-600">Credit for {previewModal.days_remaining} unused days</span>
                        <span className="font-medium text-emerald-600">${previewModal.credit_for_unused.toFixed(2)}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Total */}
              <div className={`rounded-xl p-4 border-2 ${previewModal.is_upgrade ? 'border-blue-200 bg-blue-50' : 'border-emerald-200 bg-emerald-50'}`}>
                {previewModal.is_upgrade ? (
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Amount Due Today</p>
                      <p className="text-xs text-gray-500">Prorated for {previewModal.days_remaining} remaining days</p>
                    </div>
                    <p className="text-2xl font-extrabold text-gray-900">${previewModal.final_charge.toFixed(2)}</p>
                  </div>
                ) : (
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-semibold text-emerald-800">Account Credit Added</p>
                      <p className="text-xs text-emerald-600">For {previewModal.days_remaining} unused days</p>
                    </div>
                    <p className="text-2xl font-extrabold text-emerald-700">${previewModal.credit_for_unused.toFixed(2)}</p>
                  </div>
                )}
              </div>

              {/* After change note */}
              <p className="text-xs text-gray-400 text-center">
                {previewModal.is_upgrade 
                  ? `After this cycle, you'll be billed $${previewModal.target_price}/mo for ${previewModal.target_plan}.`
                  : previewModal.target_price > 0 
                    ? `Your ${previewModal.target_plan} plan ($${previewModal.target_price}/mo) starts immediately. Credit will offset your next bill.`
                    : `Your plan will switch to ${previewModal.target_plan} (free) immediately. Credit stored for future upgrades.`
                }
              </p>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button onClick={() => setPreviewModal(null)} className="flex-1 py-3 rounded-xl text-sm font-semibold text-gray-600 border border-gray-200 hover:bg-gray-50 transition-all" data-testid="proration-cancel-btn">
                  Cancel
                </button>
                <button
                  data-testid="proration-confirm-btn"
                  onClick={previewModal.is_upgrade ? handleConfirmUpgrade : handleConfirmDowngrade}
                  className={`flex-1 py-3 rounded-xl text-sm font-semibold text-white transition-all hover:shadow-lg flex items-center justify-center gap-2 ${
                    previewModal.is_upgrade ? 'bg-blue-600 hover:bg-blue-700' : 'bg-amber-600 hover:bg-amber-700'
                  }`}>
                  {previewModal.is_upgrade ? <><Zap className="w-4 h-4" /> Confirm Upgrade</> : <><ArrowDown className="w-4 h-4" /> Confirm Downgrade</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionView;
